import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
  pdf,
} from "@react-pdf/renderer";
import logoUrl from "@/assets/logo_new.png";
import { formatPhone } from "@/lib/utils";
import Poppins600 from "@/assets/fonts/Poppins-600.ttf";
import Poppins700 from "@/assets/fonts/Poppins-700.ttf";
import Manrope400 from "@/assets/fonts/Manrope-400.ttf";
import Manrope600 from "@/assets/fonts/Manrope-600.ttf";
import Manrope700 from "@/assets/fonts/Manrope-700.ttf";
import Manrope800 from "@/assets/fonts/Manrope-800.ttf";
import Grotesk500 from "@/assets/fonts/SpaceGrotesk-500.ttf";
import Grotesk600 from "@/assets/fonts/SpaceGrotesk-600.ttf";
import Grotesk700 from "@/assets/fonts/SpaceGrotesk-700.ttf";

// ── Brand fonts: Poppins = display/prices,
// Manrope = body/UI, Space Grotesk = figures/meta ─────────────────────────────
Font.register({
  family: "Poppins",
  fonts: [
    { src: Poppins600, fontWeight: 600 },
    { src: Poppins700, fontWeight: 700 },
  ],
});
Font.register({
  family: "Manrope",
  fonts: [
    { src: Manrope400, fontWeight: 400 },
    { src: Manrope600, fontWeight: 600 },
    { src: Manrope700, fontWeight: 700 },
    { src: Manrope800, fontWeight: 800 },
  ],
});
Font.register({
  family: "SpaceGrotesk",
  fonts: [
    { src: Grotesk500, fontWeight: 500 },
    { src: Grotesk600, fontWeight: 600 },
    { src: Grotesk700, fontWeight: 700 },
  ],
});

Font.registerHyphenationCallback((word) => [word]);

// ── Palette — Dark·Gold tokens from the BL Brand Guide ───────────────────────
const BG       = "#0d0b09"; // page background
const SURFACE  = "#191410"; // raised cards / panels
const SURFACE2 = "#130f0a"; // footer / deep panels
const CREAM    = "#f4ecdd"; // primary text
const TAN      = "#a99f8c"; // secondary text
const TAN2     = "#8c8171"; // tertiary text
const TAN3     = "#6f6656"; // faint text
const GOLD     = "#d8b45c"; // accent
const GOLD2    = "#a9842b"; // deep accent
const INK      = "#1a1408"; // ink on gold
// react-pdf scrambles rgba() BORDER colors (cream renders green, green renders
// salmon) while rgba backgrounds are fine — so the brand hairlines
// (rgba(240,224,196,.10/.22)) are pre-flattened over the dark bg as solid hex.
const HAIRLINE  = "#282219";
const HAIRLINE2 = "#3F3A32";
const GREEN    = "#33a86a"; // semantic: financing / positive

// ── Styles ────────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  // paddingBottom reserves space for the fixed footer on EVERY page — without
  // it, content that wraps to page 2 flows underneath/through the footer bar.
  page: { backgroundColor: BG, padding: 0, paddingBottom: 46, fontFamily: "Manrope" },

  // ── Top alert bar (gold, dark ink text — per gold button standard) ──
  alertBar: {
    backgroundColor: GOLD,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 4,
    gap: 8,
  },
  alertText: {
    color: INK,
    fontSize: 7,
    fontFamily: "Manrope",
    fontWeight: 800,
    letterSpacing: 1.6,
    textTransform: "uppercase",
  },
  alertDot: { width: 4, height: 4, borderRadius: 2, backgroundColor: INK },

  // ── Hero ──
  headerGap: { height: 14 },
  hero: { backgroundColor: BG, paddingTop: 0, paddingBottom: 0 },
  heroInner: { flexDirection: "row", minHeight: 168 },
  heroLeft: { flex: 1, paddingHorizontal: 28, paddingBottom: 12 },
  heroImageCol: { width: 210, position: "relative" },
  heroImg: { width: 210, height: 168, objectFit: "cover" as unknown as undefined },
  heroImgOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    width: 40,
    backgroundColor: BG,
    opacity: 0.55,
  },
  heroTop: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  // Dark-theme wordmark sits directly on the dark hero — no white box.
  logo: { width: 168, height: 38 },
  acqLabel: {
    color: GOLD,
    fontSize: 8,
    fontFamily: "Manrope",
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: "uppercase",
    marginBottom: 3,
  },
  heroTitle: {
    color: CREAM,
    fontSize: 19,
    fontFamily: "Poppins",
    fontWeight: 700,
    lineHeight: 1.15,
    marginBottom: 6,
  },
  servicePills: { flexDirection: "row", flexWrap: "wrap", gap: 5, marginBottom: 6 },
  pill: {
    borderWidth: 1,
    borderColor: HAIRLINE2,
    borderRadius: 20,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  pillText: { color: "rgba(244,236,221,0.85)", fontSize: 6.5, letterSpacing: 0.4 },
  locationRow: { flexDirection: "row", alignItems: "center", gap: 4, marginBottom: 8 },
  locationPin: { width: 7, height: 7, borderRadius: 3.5, backgroundColor: GOLD },
  locationText: { color: TAN, fontSize: 7.5 },

  // ── Metrics row (on dark bg) ──
  metricsRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: HAIRLINE,
    marginTop: 2,
  },
  metricCell: {
    flex: 1,
    paddingVertical: 9,
    paddingHorizontal: 8,
    alignItems: "center",
    borderRightWidth: 1,
    borderRightColor: HAIRLINE,
  },
  metricCellLast: {
    flex: 1,
    paddingVertical: 9,
    paddingHorizontal: 8,
    alignItems: "center",
  },
  metricAccent: { width: 20, height: 2, borderRadius: 1, marginBottom: 5 },
  metricValue: { color: CREAM, fontSize: 11.5, fontFamily: "SpaceGrotesk", fontWeight: 600, textAlign: "center" },
  metricLabel: { color: TAN2, fontSize: 5.5, fontFamily: "Manrope", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.9, marginTop: 2, textAlign: "center" },

  // ── Body ──
  // No paddingBottom here: the PAGE style already reserves 46pt for the fixed
  // footer. Doubling the reserve made near-full teasers spill ~46pt of pure
  // padding onto a blank second page.
  body: { paddingHorizontal: 24, paddingTop: 12 },

  // ── Section header ──
  secRow: { flexDirection: "row", alignItems: "center", gap: 6, marginBottom: 5 },
  secIcon: { width: 8, height: 8, borderRadius: 2 },
  secTitle: { fontSize: 7.5, fontFamily: "Manrope", fontWeight: 700, color: CREAM, textTransform: "uppercase", letterSpacing: 0.6 },
  secBar: { height: 2, borderRadius: 1, marginBottom: 7 },

  // ── Two-col grid ──
  grid2: { flexDirection: "row", gap: 10, marginBottom: 8 },
  col: { flex: 1 },
  col6: { flex: 0.6 },
  col4: { flex: 0.4 },

  // ── Opportunity box ──
  opportunityBox: {
    backgroundColor: SURFACE,
    borderRadius: 6,
    padding: 8,
    borderLeftWidth: 3,
    borderLeftColor: GOLD,
  },
  opportunityText: { fontSize: 7.5, color: "rgba(244,236,221,0.9)", lineHeight: 1.55 },

  // ── Highlight checklist ──
  checkRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 5, gap: 6 },
  checkDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: GOLD,
    marginTop: 3,
    flexShrink: 0,
  },
  checkText: { fontSize: 7.5, color: CREAM, flex: 1, lineHeight: 1.4 },
  checkLabel: { fontSize: 5.5, color: TAN2, textTransform: "uppercase", letterSpacing: 0.4 },

  // ── Highlight card grid ──
  hlGrid: { flexDirection: "row", flexWrap: "wrap", gap: 5 },
  hlCard: {
    width: "47%",
    backgroundColor: SURFACE,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: HAIRLINE,
    borderTopWidth: 3,
    paddingHorizontal: 7,
    paddingVertical: 5,
  },
  hlCardLabel: { fontSize: 5.5, color: TAN2, fontFamily: "Manrope", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 },
  hlCardValue: { fontSize: 8.5, fontFamily: "SpaceGrotesk", fontWeight: 600, color: CREAM },

  // ── Financial table ──
  finTable: { borderRadius: 6, overflow: "hidden", borderWidth: 1, borderColor: HAIRLINE },
  finRow: { flexDirection: "row", paddingVertical: 5, paddingHorizontal: 9, borderBottomWidth: 1, borderBottomColor: HAIRLINE },
  finRowAlt: { flexDirection: "row", paddingVertical: 5, paddingHorizontal: 9, backgroundColor: SURFACE, borderBottomWidth: 1, borderBottomColor: HAIRLINE },
  finRowLast: { flexDirection: "row", paddingVertical: 5, paddingHorizontal: 9 },
  finLabel: { flex: 1, fontSize: 7, color: TAN },
  finValue: { fontSize: 7.5, fontFamily: "SpaceGrotesk", fontWeight: 600, color: CREAM },
  finValueGreen: { fontSize: 7.5, fontFamily: "SpaceGrotesk", fontWeight: 600, color: GREEN },

  // ── Bullet list ──
  bulletRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 4, gap: 5 },
  bulletDot: { width: 4, height: 4, borderRadius: 2, backgroundColor: GOLD, marginTop: 3 },
  bulletDotOrange: { width: 4, height: 4, borderRadius: 2, backgroundColor: GOLD2, marginTop: 3 },
  bulletText: { fontSize: 7.5, color: "rgba(244,236,221,0.9)", flex: 1, lineHeight: 1.5 },

  // ── Panel card ──
  panel: { backgroundColor: SURFACE, borderRadius: 6, padding: 9 },
  panelNavy: { backgroundColor: SURFACE2, borderRadius: 6, padding: 9 },
  panelBorder: { backgroundColor: SURFACE, borderRadius: 6, paddingHorizontal: 9, paddingVertical: 8, borderWidth: 1, borderColor: HAIRLINE },

  // ── SDE range box ──
  sdeBox: {
    backgroundColor: SURFACE,
    borderRadius: 6,
    padding: 8,
    alignItems: "center",
    marginTop: 6,
    borderWidth: 1,
    borderColor: HAIRLINE,
  },
  sdeLabel: { color: TAN2, fontSize: 6, textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 },
  sdeValue: { color: GOLD, fontSize: 13, fontFamily: "Poppins", fontWeight: 700 },
  sdeCaption: { color: TAN2, fontSize: 6, marginTop: 2 },

  // ── SBA badge (semantic green, translucent for dark) ──
  sbaBadge: {
    backgroundColor: "rgba(51,168,106,0.12)",
    borderWidth: 1,
    borderColor: "#2E6B47", // rgba border would render salmon (see note above)
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 4,
    alignSelf: "flex-start",
    marginBottom: 6,
  },
  sbaBadgeText: { color: GREEN, fontSize: 6.5, fontFamily: "Manrope", fontWeight: 700, letterSpacing: 0.7 },

  // ── Bottom section ──
  bottomRow: { flexDirection: "row", gap: 10, marginTop: 0 },
  bottomLineBox: {
    backgroundColor: SURFACE,
    borderRadius: 7,
    padding: 10,
    borderWidth: 1,
    borderColor: HAIRLINE,
  },
  bottomLineTitle: { color: GOLD, fontSize: 7, fontFamily: "Manrope", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 5 },
  bottomLineText: { color: "rgba(244,236,221,0.85)", fontSize: 7.5, lineHeight: 1.5 },

  nextStepsBox: {
    flex: 1,
    backgroundColor: SURFACE2,
    borderRadius: 7,
    padding: 10,
    borderLeftWidth: 3,
    borderLeftColor: GOLD,
  },
  nextStepsTitle: { color: CREAM, fontSize: 7, fontFamily: "Manrope", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6 },

  // ── Compact BL Advisor card (sits under Deal Highlights) ──
  advisorCard: {
    marginTop: 8,
    backgroundColor: SURFACE,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: HAIRLINE,
    borderLeftWidth: 3,
    borderLeftColor: GOLD,
    padding: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  advisorAvatar: { width: 30, height: 30, borderRadius: 15 },
  advisorInitials: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: "rgba(216,180,92,0.15)",
    alignItems: "center",
    justifyContent: "center",
  },
  advisorInitialsText: { color: GOLD, fontSize: 9.5, fontFamily: "Poppins", fontWeight: 700 },
  advisorKicker: { fontSize: 5, color: GOLD, fontFamily: "Manrope", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 1 },
  advisorName: { fontSize: 8.5, fontFamily: "Manrope", fontWeight: 700, color: CREAM, marginBottom: 1 },
  advisorLine: { fontSize: 6.5, color: TAN, marginTop: 1 },

  // ── Contact card ──
  contactCard: {
    backgroundColor: SURFACE,
    borderRadius: 5,
    padding: 7,
    marginBottom: 5,
    borderWidth: 1,
    borderColor: HAIRLINE,
  },
  contactRole: { fontSize: 5.5, color: GOLD, textTransform: "uppercase", letterSpacing: 0.9, fontFamily: "Manrope", fontWeight: 700, marginBottom: 2 },
  contactName: { fontSize: 8.5, fontFamily: "Manrope", fontWeight: 700, color: CREAM },
  contactLine: { fontSize: 7, color: TAN, marginTop: 1 },

  // ── Footer ──
  footer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: SURFACE2,
    borderTopWidth: 1,
    borderTopColor: HAIRLINE,
    paddingHorizontal: 24,
    paddingVertical: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerLeft: { color: GOLD, fontSize: 6.5, fontFamily: "Manrope", fontWeight: 700 },
  footerDisclaimer: { color: TAN3, fontSize: 5.5, marginTop: 2, maxWidth: 360 },
  footerRight: { color: TAN2, fontSize: 6.5, fontFamily: "SpaceGrotesk", fontWeight: 500 },
});

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (v: number | null) =>
  v
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v)
    : "—";

const parseLines = (text: string | null): string[] =>
  (text ?? "").split("\n").map((l) => l.trim()).filter(Boolean);

// The overview sits in a narrow column beside Deal Highlights, which only holds
// about this much before the row grows taller than a page. Anything past it
// continues full width below, so nothing is dropped.
const OVERVIEW_COL_CHARS = 2600;

// Split at the last paragraph break, else sentence end, else word boundary.
const splitOverview = (text: string | null): [string | null, string | null] => {
  if (!text) return [null, null];
  if (text.length <= OVERVIEW_COL_CHARS) return [text, null];
  const head = text.slice(0, OVERVIEW_COL_CHARS);
  const para = head.lastIndexOf("\n");
  const sentence = head.lastIndexOf(". ");
  const at = para > 0 ? para : sentence > 0 ? sentence + 1 : head.lastIndexOf(" ");
  const cut = at > OVERVIEW_COL_CHARS * 0.6 ? at : OVERVIEW_COL_CHARS;
  return [text.slice(0, cut).trim(), text.slice(cut).trim()];
};

// Bullets pair off two per row so the list can flow across pages: a row is
// short enough to move to the next page whole, and react-pdf lays out a flex
// row that has to break mid-way badly.
const pairUp = (lines: string[]): [string, string | undefined][] =>
  lines.reduce<[string, string | undefined][]>((rows, line, i) => {
    if (i % 2 === 0) rows.push([line, lines[i + 1]]);
    return rows;
  }, []);

// ── Types ─────────────────────────────────────────────────────────────────────
export interface PDFListingData {
  title: string;
  categoryName: string | null;
  overview: string | null;
  locationLabel: string | null;
  address_line: string | null;
  zip_code: string | null;
  asking_price: number | null;
  revenue: number | null;
  ebitda: number | null;
  cf_multiple?: number | null;
  established_year: number | null;
  established_month: number | null;
  website_url: string | null;
  is_sba_approved: boolean;
  seller_financing: boolean;
  additional_notes: string | null;
  agent_notes: string | null;
  agent_commission_pct: number | null;
  seller_name: string | null;
  seller_email: string | null;
  seller_phone: string | null;
  agent_name?: string;
  agent_email?: string;
  agent_phone?: string;
  agent_avatar_url?: string | null;
  imageUrl?: string | null;
}

// ── Section header component ──────────────────────────────────────────────────
function SectionHeader({ title, color }: { title: string; color: string }) {
  return (
    <>
      <View style={s.secRow}>
        <View style={[s.secIcon, { backgroundColor: color }]} />
        <Text style={s.secTitle}>{title}</Text>
      </View>
      <View style={[s.secBar, { backgroundColor: color, width: 28 }]} />
    </>
  );
}

// ── Document ──────────────────────────────────────────────────────────────────
export function ListingPDFDoc({ data }: { data: PDFListingData }) {
  const fullLocation = [data.address_line, data.locationLabel, data.zip_code]
    .filter(Boolean)
    .join(" · ");
  const year = data.established_year
    ? data.established_month
      ? `${data.established_month}/${data.established_year}`
      : String(data.established_year)
    : null;

  // The teaser runs to as many pages as the listing needs — no field is capped.
  const [overviewLead, overviewRest] = splitOverview(data.overview);
  const additionalLines = parseLines(data.additional_notes);
  const agentLines = parseLines(data.agent_notes);
  const notePairs = pairUp(additionalLines);

  // Build deal highlights card grid — gold family for money, tan for facts,
  // semantic green only for financing statuses.
  const highlights: { label: string; value: string; accent: string }[] = [];
  if (data.asking_price) highlights.push({ label: "Asking Price", value: fmt(data.asking_price), accent: GOLD });
  if (data.revenue) highlights.push({ label: "Annual Revenue", value: fmt(data.revenue), accent: GOLD });
  if (data.ebitda) highlights.push({ label: "Adj. SDE / EBITDA", value: fmt(data.ebitda), accent: GOLD });
  if (data.cf_multiple != null || (data.asking_price && data.ebitda))
    highlights.push({ label: "C.F. Multiple", value: `${(data.cf_multiple ?? data.asking_price! / data.ebitda!).toFixed(2)}X`, accent: GOLD2 });
  if (year) highlights.push({ label: "Established", value: year, accent: TAN });
  if (data.locationLabel) highlights.push({ label: "Location", value: data.locationLabel, accent: TAN });
  if (data.categoryName) highlights.push({ label: "Industry", value: data.categoryName, accent: TAN });
  if (data.is_sba_approved) highlights.push({ label: "Financing", value: "SBA Pre-Approved", accent: GREEN });
  if (data.seller_financing) highlights.push({ label: "Seller Financing", value: "Available", accent: GREEN });

  return (
    <Document title={data.title} author="BusinessLocating Hub">
      <Page size="A4" style={s.page}>

        {/* ── Alert bar ── fixed, so every page of a long teaser is stamped
            confidential and continuation pages get a top margin. */}
        <View style={s.alertBar} fixed>
          <View style={s.alertDot} />
          <Text style={s.alertText}>Confidential Business Teaser</Text>
          <View style={s.alertDot} />
        </View>
        {/* Breathing room under the fixed bar, on every page */}
        <View style={s.headerGap} fixed />

        {/* ── Hero ── */}
        <View style={s.hero}>
          <View style={s.heroInner}>
            {/* Left: text content */}
            <View style={s.heroLeft}>
              <View style={s.heroTop}>
                <Image src={logoUrl} style={s.logo} />
              </View>

              <Text style={s.acqLabel}>Acquisition Opportunity</Text>
              <Text style={s.heroTitle}>{data.title}</Text>

              {/* Service pills */}
              {data.categoryName && (
                <View style={s.servicePills}>
                  {data.categoryName.split(/[,·•\/]/).map((s2, i) => (
                    <View key={i} style={s.pill}>
                      <Text style={s.pillText}>{s2.trim()}</Text>
                    </View>
                  ))}
                </View>
              )}

              {/* Location */}
              {fullLocation && (
                <View style={s.locationRow}>
                  <View style={s.locationPin} />
                  <Text style={s.locationText}>{fullLocation}</Text>
                </View>
              )}
            </View>

            {/* Right: category image with left-edge fade overlay */}
            {data.imageUrl && (
              <View style={s.heroImageCol}>
                <Image src={data.imageUrl} style={s.heroImg} />
                <View style={s.heroImgOverlay} />
              </View>
            )}
          </View>

          {/* ── Metrics strip ── */}
          <View style={s.metricsRow}>
            <View style={s.metricCell}>
              <View style={[s.metricAccent, { backgroundColor: GOLD }]} />
              <Text style={s.metricValue}>{fmt(data.revenue)}</Text>
              <Text style={s.metricLabel}>Annual Revenue</Text>
            </View>
            <View style={s.metricCell}>
              <View style={[s.metricAccent, { backgroundColor: GOLD2 }]} />
              <Text style={s.metricValue}>{fmt(data.ebitda)}</Text>
              <Text style={s.metricLabel}>Adj. SDE / EBITDA</Text>
            </View>
            <View style={s.metricCell}>
              <View style={[s.metricAccent, { backgroundColor: GOLD }]} />
              <Text style={s.metricValue}>{data.locationLabel ?? "USA"}</Text>
              <Text style={s.metricLabel}>Market Presence</Text>
            </View>
            {(data.cf_multiple != null || (data.asking_price && data.ebitda)) && (
              <View style={s.metricCell}>
                <View style={[s.metricAccent, { backgroundColor: GOLD2 }]} />
                <Text style={s.metricValue}>
                  {(data.cf_multiple ?? (data.asking_price! / data.ebitda!)).toFixed(2)}X
                </Text>
                <Text style={s.metricLabel}>C.F. Multiple</Text>
              </View>
            )}
            <View style={s.metricCellLast}>
              <View style={[s.metricAccent, { backgroundColor: GOLD }]} />
              <Text style={[s.metricValue, { color: GOLD }]}>{fmt(data.asking_price)}</Text>
              <Text style={s.metricLabel}>Asking Price</Text>
            </View>
          </View>
        </View>

        {/* ── Body ── */}
        <View style={s.body}>

          {/* SBA / Seller Financing badges */}
          {data.is_sba_approved && (
            <View style={s.sbaBadge}>
              <Text style={s.sbaBadgeText}>SBA Pre-Approved Financing</Text>
            </View>
          )}
          {data.seller_financing && (
            <View style={s.sbaBadge}>
              <Text style={s.sbaBadgeText}>Seller Financing Available</Text>
            </View>
          )}

          {/* ── Row 1: The Opportunity + Deal Highlights ── */}
          <View style={[s.grid2, { marginBottom: overviewRest ? 8 : 16 }]}>

            {/* The Opportunity */}
            {overviewLead && (
              <View style={s.col6}>
                <SectionHeader title="The Opportunity" color={GOLD} />
                <View style={s.opportunityBox}>
                  <Text style={s.opportunityText}>{overviewLead}</Text>
                </View>
              </View>
            )}

            {/* Deal Highlights + compact BL Advisor card */}
            <View style={s.col4}>
              <SectionHeader title="Deal Highlights" color={GOLD} />
              <View style={s.hlGrid}>
                {highlights.map((h, i) => (
                  <View key={i} style={[s.hlCard, { borderTopColor: h.accent }]} wrap={false}>
                    <Text style={s.hlCardLabel}>{h.label}</Text>
                    <Text style={[s.hlCardValue, { color: h.accent === GOLD || h.accent === GOLD2 || h.accent === GREEN ? h.accent : CREAM }]}>{h.value}</Text>
                  </View>
                ))}
              </View>

              {/* Compact advisor card fills the space under the highlights */}
              {(data.agent_name || data.agent_email || data.agent_phone) && (
                <View style={s.advisorCard}>
                  {data.agent_avatar_url ? (
                    <Image src={data.agent_avatar_url} style={s.advisorAvatar} />
                  ) : (
                    <View style={s.advisorInitials}>
                      <Text style={s.advisorInitialsText}>
                        {data.agent_name
                          ? data.agent_name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()
                          : "BL"}
                      </Text>
                    </View>
                  )}
                  <View style={{ flex: 1 }}>
                    <Text style={s.advisorKicker}>BL Advisor</Text>
                    {data.agent_name && <Text style={s.advisorName}>{data.agent_name}</Text>}
                    {data.agent_email && (
                      <Text style={s.advisorLine}>
                        <Text style={{ color: GOLD, fontFamily: "Manrope", fontWeight: 700 }}>@  </Text>
                        {data.agent_email}
                      </Text>
                    )}
                    {data.agent_phone && (
                      <Text style={s.advisorLine}>
                        <Text style={{ color: GOLD, fontFamily: "Manrope", fontWeight: 700 }}>#  </Text>
                        {formatPhone(data.agent_phone)}
                      </Text>
                    )}
                  </View>
                </View>
              )}
            </View>
          </View>

          {/* Rest of a long overview, full width under the row */}
          {overviewRest && (
            <View style={[s.opportunityBox, { marginBottom: 16 }]}>
              <Text style={s.opportunityText}>{overviewRest}</Text>
            </View>
          )}

          {/* ── Bottom: Key Highlights + Additional Notes (full width) ── */}
          {(additionalLines.length > 0 || agentLines.length > 0) && (
              <View>
                {additionalLines.length > 0 && (
                  <View style={{ marginBottom: 12 }}>
                    <View minPresenceAhead={60}>
                      <SectionHeader title="Key Highlights" color={GOLD} />
                    </View>
                    {notePairs.map(([left, right], r) => (
                      <View key={r} style={[s.grid2, { gap: 10, marginBottom: 0 }]} wrap={false}>
                        <View style={s.col}>
                          <View style={s.bulletRow}>
                            <View style={s.bulletDot} />
                            <Text style={s.bulletText}>{left}</Text>
                          </View>
                        </View>
                        <View style={s.col}>
                          {right && (
                            <View style={s.bulletRow}>
                              <View style={s.bulletDot} />
                              <Text style={s.bulletText}>{right}</Text>
                            </View>
                          )}
                        </View>
                      </View>
                    ))}
                  </View>
                )}
                {agentLines.length > 0 && (
                  <View style={s.bottomLineBox} minPresenceAhead={60}>
                    <Text style={s.bottomLineTitle}>Additional Notes</Text>
                    {agentLines.map((line, i) => (
                      <View key={i} style={[s.bulletRow, { marginBottom: 4 }]}>
                        <View style={[s.bulletDotOrange, { marginTop: 3 }]} />
                        <Text style={s.bulletText}>{line}</Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
          )}
        </View>

        {/* ── Footer ── */}
        <View style={s.footer} fixed>
          <View>
            <Text style={s.footerLeft}>BusinessLocating Hub · businesslocating.com</Text>
            <Text style={s.footerDisclaimer}>
              THIS IS CONFIDENTIAL AND INTENDED FOR QUALIFIED BUYERS ONLY.{"\n"}
              Please do not distribute. All inquiries will be handled with the utmost confidentiality.
            </Text>
          </View>
          <Text style={s.footerRight} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>

      </Page>
    </Document>
  );
}

// ── Export trigger ────────────────────────────────────────────────────────────
export async function downloadListingPDF(data: PDFListingData) {
  const blob = await pdf(<ListingPDFDoc data={data} />).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${data.title.replace(/[^a-zA-Z0-9]/g, "_")}_BusinessTeaser.pdf`;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
